import React, { useEffect, useMemo, useRef, useState } from 'react';
import { searchNodes } from '../../utils/mindmap/nodeSearch';

const MapSearchPanel = ({ nodes, isDarkMode, onClose, onSelectResult }) => {
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const results = useMemo(() => searchNodes(nodes, query), [nodes, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(results.length - 1, 0)));
  }, [results.length]);

  const handleKeyDown = (event) => {
    event.stopPropagation();
    if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'f') {
      event.preventDefault();
      inputRef.current?.select();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'ArrowDown' && results.length) {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
      return;
    }
    if (event.key === 'ArrowUp' && results.length) {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + results.length) % results.length);
      return;
    }
    if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      onSelectResult(results[activeIndex].node.id);
    }
  };

  const foreground = isDarkMode ? '#f7f7f8' : '#171719';
  const surface = isDarkMode ? 'rgba(39, 39, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)';
  const border = isDarkMode ? '#52525b' : '#d4d4d8';

  return (
    <div
      role="dialog"
      aria-label="Search map"
      data-export-ignore="true"
      onMouseDown={(event) => event.stopPropagation()}
      style={{
        position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
        width: 'min(520px, calc(100vw - 32px))', zIndex: 160, color: foreground,
        background: surface, border: `1px solid ${border}`, borderRadius: 12,
        boxShadow: '0 16px 50px rgba(0, 0, 0, 0.28)', overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10 }}>
        <span aria-hidden="true">⌕</span>
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
          onKeyDown={handleKeyDown}
          placeholder="Search every node…"
          aria-label="Search every node"
          style={{
            flex: 1, border: 0, outline: 0, background: 'transparent', color: foreground,
            fontSize: 16, minWidth: 0,
          }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search"
          style={{ border: 0, background: 'transparent', color: foreground, cursor: 'pointer', fontSize: 18 }}
        >×</button>
      </div>
      {query && (
        <div role="listbox" aria-label="Node search results" style={{ borderTop: `1px solid ${border}`, maxHeight: 320, overflowY: 'auto' }}>
          {results.map((result, index) => (
            <button
              key={result.node.id}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => onSelectResult(result.node.id)}
              style={{
                display: 'block', width: '100%', padding: '10px 14px', border: 0,
                borderBottom: index < results.length - 1 ? `1px solid ${border}` : 0,
                background: index === activeIndex
                  ? (isDarkMode ? '#3f3f46' : '#f4f4f5')
                  : 'transparent',
                color: foreground, textAlign: 'left', cursor: 'pointer',
              }}
            >
              <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {result.label}
              </span>
              {result.node.linkUrl && (
                <span style={{ display: 'block', fontSize: 12, opacity: 0.65, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {result.node.linkUrl}
                </span>
              )}
            </button>
          ))}
          {!results.length && <div style={{ padding: 14, opacity: 0.7 }}>No matching nodes</div>}
        </div>
      )}
      <div style={{ padding: '6px 12px', borderTop: query ? `1px solid ${border}` : 0, fontSize: 11, opacity: 0.65 }}>
        ↑↓ navigate · Enter reveal · Esc close
      </div>
    </div>
  );
};

export default MapSearchPanel;
